/**
 * Os textos e os formatadores que **todas** as telas de Negócio compartilham.
 *
 * Ele nasceu na 3.9, e nasceu por uma falha concreta: o quadro do Funil (3.8) e o painel do dia
 * (3.9) escrevem as mesmas frases sobre o mesmo dado — "sem orçamento", "valor não visível para
 * SDR", "sem próximo passo" — e formatam o mesmo dinheiro com o mesmo arredondamento. Os
 * docstrings dos dois arquivos **afirmavam** que as telas dizem a mesma coisa, e nada as prendia:
 * mudar a frase no quadro deixaria o painel escrevendo a antiga, com as duas suítes verdes. É o
 * mesmo problema que o serviço resolveu tornando `dinheiro` e `dias_entre` públicos em vez de
 * copiá-los para o módulo do painel.
 *
 * **O que mora aqui, e o que deliberadamente não mora.** Aqui ficam os **textos** e os
 * **formatadores** — o que precisa ser literalmente igual nas duas telas. Não ficam componentes,
 * nem a escada de `origemDoTitulo`, nem view-model nenhum: cada tela desenha o que ela desenha, e
 * um componente comum ali obrigaria as duas a concordarem sobre arranjo, que é justamente onde
 * elas divergem (coluna × linha, card × cartão empilhado).
 *
 * **`TITULO_DA_COR` é derivado de `descreverAsFaixas`, e não escrito.** Aquela função é a fonte
 * única dos três rótulos por extenso ("Em dia" / "Esfriando" / "Abandonado"), e é ela que o cartão
 * de Administração já usa. Os cortes passados são irrelevantes de propósito: o `titulo` de cada
 * faixa não depende deles — só o `intervalo` depende —, e é por isso que o rótulo pode ser
 * resolvido uma vez, no módulo, sem esperar `GET /regua`.
 */

import { descreverAsFaixas } from '@/modulos/negocios/ReguaDeDias'
import type {
  NegocioNoQuadro,
  NegocioOriginado,
  RecorteDoPrazo,
  StatusDaRat,
} from '@/modulos/negocios/api'

/** O que a tela escreve no lugar do orçamento que ainda não existe (protótipo `:1129`). */
export const SEM_ORCAMENTO = 'sem orçamento'

/**
 * O que a SDR lê no lugar do dinheiro — a frase do protótipo, literal (`:1129`).
 *
 * Ela é uma **frase**, e não um traço: o traço faria parecer que aquele Negócio não tem orçamento,
 * e a tela mentiria sobre o dado em vez de explicar o recorte. O corpo que chega à SDR nem carrega
 * a chave (FR-62), então não há valor a esconder aqui — o que há é a razão de o campo não estar lá.
 */
export const VALOR_INVISIVEL = 'valor não visível para SDR'

/** O slot do Épico 4 (protótipo `:1134`). Sem Tarefa, ele é o único texto possível. */
export const SEM_PROXIMO_PASSO = 'sem próximo passo'

/**
 * Dinheiro em BRL, **sem centavos** — e o arredondamento é deliberado, apesar de o contrato
 * inteiro existir para não perder centavo.
 *
 * As duas coisas convivem porque medem propósitos diferentes. O serviço manda `"148000.00"` como
 * **texto de duas casas** justamente para o centavo atravessar a fronteira intacto: quem for
 * editar, conferir ou somar aquele número — a ficha do Negócio (3.10), o relatório do Épico 7 —
 * recebe o valor exato. O que este formatador produz é a leitura **de relance**, que é o que o
 * protótipo desenha (`brl()`, `.dc.html:932`): `R$ 148.000` cabe na coluna e diz o que precisa
 * dizer; `R$ 148.000,00` gasta quatro caracteres para repetir dois zeros.
 *
 * O preço está declarado: `"2500.50"` aparece como `R$ 2.501`. Estas telas **arredondam**, e não
 * são fonte para conferência de centavo — quem precisa do valor exato abre a ficha.
 */
export const MOEDA = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 0,
})

/**
 * Dinheiro em BRL **com as duas casas exatas** — o formatador da ficha (3.10).
 *
 * Ele é o par de `MOEDA`, e a diferença entre os dois é o propósito, escrito acima: o card e a
 * lista do dia arredondam porque leem de relance; a ficha é onde alguém confere, e o parágrafo de
 * `MOEDA` já prometia por escrito que "quem precisa do valor exato abre a ficha". Este é o
 * formatador que cumpre a promessa.
 *
 * Ele mora **aqui**, e não em `FichaDoNegocio.tsx`, pela razão de sempre: o par só é legível junto
 * — quem entrar para mexer no arredondamento precisa ver os dois lados da decisão de uma vez —, e
 * `apresentacao.test.ts` é o único arquivo que compara os textos e os formatos com o literal.
 *
 * `"2500.50"` sai como `R$ 2.500,50`, e `"148000.00"` como `R$ 148.000,00`.
 */
export const MOEDA_EXATA = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Contagem em português do Brasil (NFR-8): milhar com ponto. */
export const NUMERO = new Intl.NumberFormat('pt-BR')

/**
 * Só a data, no fuso de quem trabalha (NFR-8) — o formato dos campos de data do FR-13.
 *
 * Ele morava em `FichaDoNegocio.tsx` e subiu para cá na 3.11, pela mesma razão de
 * `ROTULO_DO_STATUS`: a lista de Originados mostra as **mesmas** duas datas (início e conclusão) do
 * **mesmo** Negócio, e duas cópias do formatador divergiriam no primeiro ajuste — a ficha diria
 * `04/03/2026` e a lista, `4 de março`.
 *
 * **O fuso é escrito, e não o do navegador.** `America/Sao_Paulo` é onde se trabalha; sem ele, a
 * mesma `data_de_conclusao` viraria a véspera para quem abrisse a tela de outro fuso — e "ganho em
 * 28/02" e "ganho em 01/03" caem em meses diferentes no relatório que o Épico 7 vai somar.
 */
export const DATA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'America/Sao_Paulo',
})

/**
 * A data **com a hora**, no fuso de quem trabalha (NFR-8) — o carimbo de um instante gravado.
 *
 * **Ela morava dentro de `FichaDoNegocio.tsx` e subiu para cá na 6.3**, pela mesma regra que criou
 * este arquivo e que já moveu `ROTULO_DO_STATUS` na 3.11: o cartão de RAT escreve o **mesmo**
 * carimbo sobre o mesmo tipo de dado (o `criado_em` de cada reclamação) que o histórico de
 * movimentações da ficha, e uma segunda cópia divergiria da primeira no dia em que alguém
 * acrescentasse os segundos — o histórico diria uma coisa e o cartão ao lado, outra, com as duas
 * suítes verdes.
 *
 * Ela é separada de `DATA`, e não `DATA` com opções, porque as duas respondem perguntas diferentes:
 * `DATA` desenha a data **pura** de um prazo ou de um início (dia, sem hora — a hora ali seria um
 * dado inventado), e esta desenha o instante em que alguma coisa aconteceu.
 */
export const DATA_E_HORA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'America/Sao_Paulo',
})

/**
 * Uma data **pura** (`'2026-09-10'`) por extenso, sem passar por `Date` (4.3, FR-18).
 *
 * **Ela não usa `DATA`, e a ausência é o ponto inteiro desta função.** `DATA` recebe um `Date`, e
 * `new Date('2026-09-10')` é meia-noite **UTC** — que em São Paulo já é o dia 9. O prazo da Tarefa
 * chega do serviço como data pura, sem hora e sem fuso, e passá-lo por `DATA` faria toda tarefa
 * marcada para o dia 10 aparecer como 09/09 na tela. `apresentacao.test.ts` prova exatamente esse
 * deslocamento ao lado do teste de `DATA`, e é ele que impede alguém "simplificar" as duas numa só.
 *
 * A conversão é textual porque o dado é texto: o serviço promete `YYYY-MM-DD` (é o que o
 * `openapi.json` declara como `format: date`), e reordenar três pedaços não precisa de calendário
 * nenhum. Não há fuso a aplicar — uma data pura não está em fuso nenhum, e é isso que ela é.
 *
 * O que não casa com o formato volta **verbatim**: um texto inesperado na tela é feio, e um
 * `Invalid Date` ou um `undefined/undefined/` é pior — ele parece que a tarefa não tem prazo,
 * quando o que houve foi o contrato mudar de forma.
 */
/**
 * O dia corrente em `America/Sao_Paulo`, no formato do `<input type="date">` (`YYYY-MM-DD`).
 *
 * **Ela mora aqui, e não em cada tela, pela razão que este arquivo inteiro existe**: a 6.4 e a 6.5
 * a escreveram cada uma por si, e duas cópias divergem no primeiro ajuste — a ficha diria um dia e
 * a lista ao lado, outro, com as duas suítes verdes.
 *
 * **O fuso é escrito, e não o do navegador.** Quem registra a entrega está em São Paulo; sem ele,
 * o teto do campo seria o dia de quem abriu a tela de outro fuso.
 *
 * `en-CA` não é escolha de idioma: é o locale cujo formato de data já é `YYYY-MM-DD`, que é o que
 * o controle nativo e o contrato esperam.
 */
export function hojeEmSaoPaulo(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date())
}

export function dataPuraPorExtenso(pura: string): string {
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(pura)
  if (partes === null) {
    return pura
  }
  const [, ano, mes, dia] = partes
  return `${dia}/${mes}/${ano}`
}

/**
 * `cor -> rótulo por extenso`, **derivado** de `descreverAsFaixas` — a fonte única dos três.
 *
 * Antes havia um mapa escrito à mão com os mesmos três textos que aquela função já devolve em
 * `titulo`. Eram duas fontes para a mesma frase, e a segunda divergiria da primeira no dia em que
 * alguém renomeasse uma faixa no cartão de Administração: a legenda diria uma coisa e o card, ao
 * lado, outra.
 */
export const TITULO_DA_COR = Object.fromEntries(
  descreverAsFaixas(0, 0).map((faixa) => [faixa.cor, faixa.titulo] as const),
) as Record<NegocioNoQuadro['cor'], string>

/** O rótulo por extenso da cor — é ele que carrega a informação do ponto `aria-hidden`. */
export function sinalDaCor(cor: NegocioNoQuadro['cor']): string {
  return TITULO_DA_COR[cor]
}

/**
 * O teto do corpo da descrição da perda (`TAMANHO_DA_DESCRICAO_DA_PERDA` do serviço, 11.3) — cópia
 * declarada: `maxLength` é restrição de validação, e o `openapi-typescript` gera o **tipo**, e não
 * o número. (Os tetos de Frete e Negociação saíram na 13.16, com os dois campos.)
 */
export const TAMANHO_DA_DESCRICAO_DA_PERDA = 2000

/**
 * O teto do **Título** da Tarefa (11.6: ex-assunto) — `TAMANHO_DO_TITULO` do serviço. É o
 * `maxLength` do campo: o texto além do teto viraria um `422` genérico depois do envio.
 */
export const TAMANHO_DO_TITULO = 120

/** O teto da descrição opcional da Tarefa (11.6) — `TAMANHO_DA_DESCRICAO` do serviço. */
export const TAMANHO_DA_DESCRICAO = 4000

/**
 * Os dois tetos da RAT (6.1), pela mesma regra dos três acima.
 *
 * A fonte é `TAMANHO_DA_DESCRICAO_DO_PROBLEMA` e `TAMANHO_DO_NUMERO_DA_OP`
 * (`servicos/negocios/dominio/rat.py`), que o serviço reusa nas colunas **e** no corpo de abertura
 * — o teto da borda é o mesmo número da coluna de propósito, porque um teto a mais viraria violação
 * de coluna no commit e apareceria como `500` no meio de uma abertura que já passou por todas as
 * guardas.
 *
 * A descrição é longa (quatro mil) porque é texto livre de reclamação; a OP é curta (sessenta)
 * porque é um identificador digitado à mão — e é justamente por ser digitado à mão que o campo
 * precisa parar de aceitar tecla em vez de deixar o `422` genérico responder por ele.
 */
export const TAMANHO_DA_DESCRICAO_DO_PROBLEMA = 4000
export const TAMANHO_DO_NUMERO_DA_OP = 60

/**
 * O que a linha do histórico diz quando o Negócio **atravessou** de um Funil para outro (FR-6).
 *
 * O handoff não é um campo do contrato: a travessia é `de_funil_id !== para_funil_id`, e quem
 * compara os dois é quem lê. Este é o rótulo que a comparação vira na tela — e ele é uma **marca**
 * ao lado do par de Etapas, e não um texto no lugar dele: o "de onde para onde" continua sendo a
 * informação, e a travessia é o que a qualifica.
 */
export const ROTULO_DE_HANDOFF = 'entrega entre funis'

/**
 * O texto do dinheiro em tela — os **três** estados, e não dois.
 *
 * `undefined` é o recorte da SDR (a chave nem vem no corpo, FR-62); `null` é "não há valor", que é
 * estado legítimo — um Negócio em andamento sem orçamento, um Orçamento que o ERP mandou sem o
 * total. Colapsar os dois faria a SDR ler que aquilo não tem valor, e a tela mentiria sobre o dado
 * em vez de explicar o recorte.
 *
 * `recortado` é um **booleano**, e não uma comparação com o texto de tela: estilo decidido por
 * igualdade de frase quebra em silêncio no dia em que alguém a reescrever.
 *
 * Ela mora aqui, e não dentro da ficha, desde a 5.3: a tela espelho do Orçamento
 * (`modulos/integracao-erp/OrcamentoDoNegocio.tsx`) lê os **mesmos** três estados do **mesmo**
 * contrato (`valor?: string | null`), e duas cópias divergiriam na primeira mudança de texto — a
 * ficha diria uma coisa sobre o recorte e o cartão ao lado, outra.
 */
export function textoDoValor(valor: string | null | undefined): {
  texto: string
  recortado: boolean
} {
  if (valor === undefined) {
    return { texto: VALOR_INVISIVEL, recortado: true }
  }
  if (valor === null) {
    return { texto: SEM_ORCAMENTO, recortado: false }
  }
  return { texto: MOEDA_EXATA.format(Number(valor)), recortado: false }
}

/**
 * O estado vazio **real** da lista de tarefas do Negócio (4.2).
 *
 * **Ele deixou de anunciar o Épico 4 nesta história**, e a troca é a história inteira em uma
 * linha: a seção era reservada, e o texto dizia de onde o conteúdo viria; agora o conteúdo existe,
 * e o que falta dizer é o que a ausência significa — não há tarefa **neste** Negócio, e o
 * formulário abaixo cria a primeira.
 *
 * Ele continua morando aqui, e não no componente, pelo mesmo motivo dos textos acima:
 * `apresentacao.test.ts` é o único arquivo que os compara com o literal, e sem isso a suíte da
 * tela mediria o módulo contra ele mesmo.
 *
 * E ele continua sendo **um** texto, e não dois: ao contrário do catálogo de Tipos (que separa
 * "crie o primeiro" de "só o Admin pode"), criar tarefa é dos quatro perfis (FR-61-A) — o
 * formulário abaixo aparece para quem quer que abra a ficha, e mandar criar é sempre verdade.
 */
export const SEM_TAREFAS = 'Nenhuma tarefa neste negócio. Crie a primeira abaixo.'

/**
 * O estado vazio **do filtro ligado** (4.3) — e ele não é o mesmo de `SEM_TAREFAS`.
 *
 * Com o chip "Atrasadas" ligado num Negócio que tem dez tarefas em dia, a lista volta vazia — e
 * `SEM_TAREFAS` diria "Nenhuma tarefa neste negócio. Crie a primeira abaixo." sobre um Negócio
 * cheio delas. A tela mentiria duas vezes na mesma frase: afirmaria ausência onde há dez linhas, e
 * mandaria criar a primeira de uma lista que já existe.
 *
 * A distinção é a mesma que separa "não há" de "ainda não sei" nos três estados da leitura, um
 * degrau adiante: aqui o vazio é **do recorte**, e a frase precisa dizer isso — a boa notícia é
 * que nada está atrasado, e o que há para fazer é desligar o filtro, e não criar tarefa.
 */
export const SEM_TAREFAS_ATRASADAS = 'Nenhuma tarefa atrasada neste negócio.'

/**
 * O que o leitor de tela lê ao lado do prazo vencido (4.3, FR-18).
 *
 * **A cor nunca é o único portador do significado**, e esta constante é a metade que sobra quando
 * ela não chega: o vermelho e o negrito dizem "atrasada" a quem enxerga, e este texto diz o mesmo
 * a quem não enxerga. É o mesmo par de `.ficha__dias` com `.ficha__so-para-leitor` que a linha de
 * dias sem contato já usa desde a 3.10 — e a razão é a mesma: um `data-vencida` sem texto ao lado
 * é uma informação que só existe para parte das pessoas.
 *
 * Ele começa com um separador porque entra **colado** ao prazo, dentro do mesmo elemento: sem ele,
 * o leitor de tela emendaria "10/09/2026atrasada".
 */
export const PRAZO_VENCIDO = ' · atrasada'

/**
 * A recusa local do prazo em branco, no molde das duas do título e do Responsável.
 *
 * O `prazo` é **obrigatório** no serviço (FR-18), e um `<input type="date">` vazio manda `''` — que
 * voltaria como o `422` genérico do envelope, o que não diz qual campo faltou. Recusar aqui não
 * gasta uma ida à rede com o que o cliente já sabe, e o servidor continua sendo quem decide.
 *
 * Ele mora aqui, e não no componente como `FALTA_TITULO` e `FALTA_RESPONSAVEL`, porque chegou
 * junto dos dois textos acima — que já teriam de morar neste arquivo de qualquer forma. Separá-lo
 * deles espalharia o vocabulário do prazo por dois lugares.
 */
export const FALTA_PRAZO = 'Informe o prazo da tarefa.'

/* --- A conclusão da Tarefa, com o Resultado opcional (4.4, FR-17) ------------------- */

/**
 * O teto do texto livre do Resultado — **espelho** da coluna do serviço, e não um número novo.
 *
 * Ele é 120, e não os 60 do rótulo de catálogo: o rótulo copiado cabe nele com folga, e quem
 * precisa do espaço é o "Outros" — a frase que quem concluiu escreveu. Ele mora aqui pela mesma
 * razão de `TAMANHO_DO_TITULO`: é o `maxLength` de um controle, e o que ele impede é a pessoa
 * digitar cento e vinte e um caracteres para receber um `422` depois de já ter escrito.
 *
 * `apresentacao.test.ts` é quem o prende ao literal — e é lá, e não aqui, que a divergência com o
 * `TAMANHO_DO_RESULTADO` do domínio apareceria.
 */
export const TAMANHO_DO_RESULTADO = 120

/**
 * O subtítulo do modal de conclusão — a frase do FR-17, literal (protótipo `:999`).
 *
 * Ela é a **primeira** coisa que quem abre o modal lê, e o que ela existe para impedir é o
 * abandono: sem a frase, os dois botões e a lista de chips fazem o modal parecer um formulário
 * obrigatório, e a conclusão mais comum do dia — a que não tem desfecho a registrar — passaria a
 * custar uma decisão que ninguém pediu.
 */
export const RESULTADO_E_OPCIONAL = 'Registrar o resultado é opcional.'

/** O rótulo do grupo de chips (protótipo `:734`). O "— opcional" repete o subtítulo de propósito:
 * quem navega por leitor de tela chega ao grupo sem ter passado pelo subtítulo. */
export const ROTULO_DO_RESULTADO = 'Resultado — opcional'

/**
 * O chip que **não** é linha do catálogo (FR-17, protótipo `:740`).
 *
 * "Outros" é opção da tela: a semente do FR-15 não a tem, e a Administração não a mostra. Escolhê-lo
 * abre o campo de texto, e o que se envia é `resultado_livre` — o serviço grava o texto no mesmo
 * `resultado_nome`, com a FK nula.
 */
export const RESULTADO_OUTROS = 'Outros'

/** O `placeholder` do campo de texto livre (protótipo `:741`). */
export const DESCREVA_O_DESFECHO = 'Descreva o desfecho'

/**
 * O que o modal escreve quando aquele Tipo não tem Resultado **ativo** nenhum.
 *
 * Estado real, e não defensivo: o Admin remove Resultados por `ativo = false` (FR-16), e um Tipo
 * pode ficar sem nenhum. Sem esta frase, o grupo de chips apareceria vazio e "Outros" pareceria a
 * única opção existente — quando o que houve foi o vocabulário daquele Tipo ter sido esvaziado.
 *
 * Ela **não** manda parar: concluir continua sendo possível, com "Outros" ou sem Resultado nenhum.
 */
export const SEM_RESULTADOS_DO_TIPO =
  'Este tipo não tem resultados cadastrados. Conclua sem resultado, ou descreva o desfecho em ' +
  '"Outros".'

/**
 * O botão que conclui **sem** sugerir o próximo passo (protótipo `:1000`).
 *
 * Os **dois** botões concluem, e o que os separa é o que vem depois do `200`: este termina ali, e
 * o primário abre o modal de próximo passo (4.5). Os rótulos nasceram finais na 4.4 por isso —
 * escrever um "Concluir" genérico e renomeá-lo depois mudaria o texto debaixo da mão de quem já
 * aprendeu onde clicar, e o rótulo é o contrato com quem clica: quem escolhe este pediu para
 * terminar, e receber a pergunta seguinte mesmo assim seria pedágio no lugar de convite.
 */
export const CONCLUIR_SEM_RESULTADO = 'Concluir sem resultado'

/**
 * O botão primário (protótipo `:1000`) — e a sugestão que ele promete é a que ele entrega desde a
 * 4.5: depois do `200`, e só por ele, abre o modal `TITULO_DO_PROXIMO_PASSO`.
 */
export const CONCLUIR_E_SUGERIR = 'Concluir e sugerir próximo passo'

/**
 * O prefixo da marca de concluída na linha da lista — a data entra depois dele.
 *
 * Ele é um **prefixo**, e não a frase montada, porque quem monta é a linha: ela tem a data em mãos
 * e o formatador certo para ela. E o formatador certo é `DATA`, e não `dataPuraPorExtenso`:
 * `concluida_em` é um **instante** (`timestamptz`), e não uma data pura como o `prazo`. Trocar os
 * dois é o engano que a 4.3 já documentou dos dois lados — `new Date('2026-09-10')` é 09/09 em São
 * Paulo, e um instante passado pelo conversor textual não casaria com o padrão e sairia verbatim.
 */
export const TAREFA_CONCLUIDA_EM = 'Concluída em'

/* --- O próximo passo (4.5, FR-21, FR-22) ------------------------------------------- */

/**
 * Os dias que a sugestão adianta — **um** número, lido pela frase e pelo prazo.
 *
 * Ele é constante, e não dois literais, porque a tela não pode dizer "em 3 dias" e preencher outra
 * data: a frase é a promessa que quem clica lê, e o prazo é o que a Tarefa herda. Com um número
 * escrito em cada lugar, mudar a régua para cinco deixaria o texto antigo no modal e o formulário
 * preenchido com a nova — e as duas suítes verdes, porque cada lado concorda consigo mesmo.
 *
 * O valor é o do protótipo (`:1338`, `"Sugestão: " + tipoTarefa + " em 3 dias"`), e a 4.5 não o
 * inventa: três dias é o intervalo que o desenho aprovou para o próximo contato.
 */
export const DIAS_DA_SUGESTAO = 3

/** O título do segundo modal do UX-DR6 (protótipo `:1332`). */
export const TITULO_DO_PROXIMO_PASSO = 'Próximo passo'

/**
 * O subtítulo do modal (protótipo `:1332`) — e ele é o FR-21 em duas frases.
 *
 * A primeira metade diz que a conclusão **já aconteceu**: o modal só existe depois do `200`, e sem
 * essa palavra quem o visse pensaria que ainda falta confirmar alguma coisa. A segunda é o convite
 * do requisito: o sistema sugere e a pessoa decide — não há aqui nada a cumprir.
 */
export const PROXIMO_PASSO_VOCE_DECIDE = 'Tarefa concluída. O sistema sugere, você decide.'

/**
 * A frase de apoio do cartão da sugestão — o protótipo **menos** a promessa de relatório.
 *
 * O protótipo escreve `... — e ele passa a aparecer no relatório "Sem próximo passo".`, e essa
 * segunda metade é do Épico 7: o KPI homônimo da tela Hoje é hoje um número **fixo**, com a nota
 * de que as tarefas chegam no Épico 4 (`PainelDeHoje.tsx:188-194`), e o slot do quadro é a frase
 * de `SEM_PROXIMO_PASSO`. Escrevê-la aqui faria a tela prometer uma consequência que nenhuma outra
 * tela mostra — exatamente o tipo de promessa que `SEM_ORCAMENTO` trata com uma frase honesta em
 * vez de um dado inventado. Fica a metade que é verdade e que o FR-21 exige de qualquer forma:
 * sugerir não é obrigar, e recusar não pede justificativa. A outra volta com o relatório.
 */
export const SUGESTAO_NAO_OBRIGA =
  'O sistema sugere, não obriga. Recusar deixa o negócio sem tarefa agendada.'

/**
 * A saída que **recusa** (protótipo `:1332`), e ela ocupa o lugar do "Cancelar" no rodapé.
 *
 * O rótulo diz o efeito, e não o gesto: "Cancelar" ali sugeriria desfazer a conclusão que acabou
 * de acontecer. Recusar não custa nada (FR-21) — nem justificativa, nem confirmação —, e é a mesma
 * saída de `Esc` e do clique no fundo.
 */
export const DEIXAR_SEM_TAREFA = 'Deixar sem tarefa'

/**
 * O primário do modal (protótipo `:1332`) — e ele **não** cria a Tarefa.
 *
 * "Agendar" é o que a pessoa quer fazer; o que o botão faz é pré-preencher o formulário que já
 * está no cartão, com o Tipo e o prazo da sugestão, e devolver o cursor ao Título. A criação
 * continua saindo do submit humano, que é a forma literal do FR-22 — um `POST` daqui seria o
 * sistema criando a Tarefa, com uma confirmação por cima, mas criando.
 */
export const AGENDAR_SUGESTAO = 'Agendar sugestão'

/**
 * A frase do cartão de destaque: `Sugestão: {Tipo} em 3 dias` (protótipo `:1338`).
 *
 * O Tipo entra **como está** — capitalizado, do `tipo_nome` congelado no registro (FR-69) —, e não
 * em minúsculas como no título do modal de conclusão: lá ele é palavra dentro de uma frase
 * ("Concluir ligação"), e aqui ele é o **nome** do que se sugere, depois de dois-pontos.
 *
 * O número sai de `DIAS_DA_SUGESTAO`, e é a mesma constante que `prazoSugerido` soma: é isto que
 * impede a tela de prometer três dias e preencher outra data.
 *
 * Ela mora aqui, e não no componente, pelo motivo do arquivo: a tela de Tarefas (4.6) abre o mesmo
 * modal, e duas cópias divergiriam no primeiro ajuste.
 */
export function sugestaoDoProximoPasso(
  tipoNome: string,
  dias: number = DIAS_DA_SUGESTAO,
): string {
  //: 11.6 (FR-21): a sugestão vem do serviço — configurada por Resultado ("Não atendida →
  //: Ligação em 1 dia") ou a regra padrão. Um dia é "amanhã", como o protótipo escreve.
  return dias === 1 ? `Sugestão: ${tipoNome} amanhã` : `Sugestão: ${tipoNome} em ${String(dias)} dias`
}

/** Se o ano tem 29 de fevereiro — a regra completa, com o século e o quadricentenário. */
function ehBissexto(ano: number): boolean {
  return ano % 4 === 0 && (ano % 100 !== 0 || ano % 400 === 0)
}

/**
 * A recusa das duas puras do prazo — e ela **estoura**, em vez de devolver um valor de piso.
 *
 * Um mês zero ou uma parte de data ausente não são estado a tratar: são sinal de que o formatador
 * ou o ambiente mudaram debaixo da função. O que o piso produziria é pior que a exceção — um
 * `'0000-00-03'` casa com `/^\d{4}-\d{2}-\d{2}$/`, é **descartado calado** pelo
 * `<input type="date">` (o campo fica vazio, como se a sugestão não tivesse preenchido nada) e
 * ainda cairia no ramo de 31 dias de `diasDoMes`. Uma data impossível que passa por bem-formada é
 * exatamente o tipo de coisa que ninguém vê até o `422` do serviço.
 */
export const DATA_IMPOSSIVEL = 'A data de hoje em São Paulo não pôde ser lida.'

/**
 * Quantos dias aquele mês tem. Sem tabela indexada: o índice fora da lista devolveria `undefined`
 * e obrigaria a um piso que esconderia o engano.
 *
 * O mês fora de 1..12 **estoura**: ele não é entrada possível de um calendário, e responder 31
 * para o mês zero faria a soma de dias atravessar a virada com uma aritmética que ninguém
 * escreveu. Quem chama é `prazoSugerido`, e a única forma de o mês sair da faixa é a leitura das
 * partes ter falhado antes.
 *
 * **O `Number.isInteger` é a metade que as comparações não fazem:** `NaN < 1` e `NaN > 12` são as
 * duas `false`, então um mês que não é número atravessaria as duas e cairia no ramo de 31 dias —
 * exatamente o "responder 31 para o mês impossível" que esta guarda existe para impedir.
 *
 * **Ela é exportada por causa desse ramo**, e não porque alguém de fora precise do calendário: com
 * `partesDaData` estourando primeiro, nenhuma chamada a `prazoSugerido` alcança um mês fora da
 * faixa, e um ramo que só um ambiente quebrado alcança é um ramo que ninguém observa. É a mesma
 * razão pela qual `recusaLocal` e `recusaDoDesfecho` são puras de módulo em `TarefasDoNegocio.tsx`.
 */
export function diasDoMes(ano: number, mes: number): number {
  if (!Number.isInteger(mes) || mes < 1 || mes > 12) {
    throw new RangeError(DATA_IMPOSSIVEL)
  }
  if (mes === 2) {
    return ehBissexto(ano) ? 29 : 28
  }
  return mes === 4 || mes === 6 || mes === 9 || mes === 11 ? 30 : 31
}

/** O dia de São Paulo em três números — `{ ano, mes, dia }`. */
export interface DataPura {
  ano: number
  mes: number
  dia: number
}

/**
 * As três partes de uma data a partir do que `Intl.DateTimeFormat.formatToParts` devolveu.
 *
 * **Ela é pura e recebe o array**, e não o `Date`, para que o ramo da ausência tenha como ser
 * alcançado: o formatador de verdade (`DATA`) declara `year`, `month` e `day`, então nenhuma
 * encenação de tela produz partes incompletas — e um ramo que só um ambiente quebrado alcança é
 * um ramo que ninguém observa. Com o array como parâmetro, a suíte entrega as partes que faltam.
 *
 * A falta **estoura**, e é essa a diferença para a versão que devolvia zeros: um `'0000-00-03'`
 * atravessa a validação de forma, some no `<input type="date">` sem uma palavra e só reaparece
 * como um `422` do serviço — a recusa mais cara possível, e a mais longe da causa.
 *
 * **E "faltar" tem duas formas, não uma.** A parte pode não vir (`undefined`) ou vir sem ser
 * número — um formatador que devolvesse o mês por extenso (`'set.'`) ou dígitos não latinos faz
 * `Number(...)` responder `NaN`, e `NaN !== undefined`: só o teste de ausência deixaria passar
 * justamente o caso que motiva esta função, produzindo um `'2026-NaN-13'` que o campo de data
 * descarta calado. `Number.isInteger` recusa as duas de uma vez.
 *
 * **E ser inteiro não basta: a faixa é a outra metade da guarda.** Um dia `0`, um dia `45` ou um
 * ano `0` são inteiros, e atravessariam a checagem acima para produzir exatamente o que ela existe
 * para impedir — o dia `0` vira uma data plausível e errada, o `45` cai três semanas adiante e um
 * dia negativo produz `'2026-09--2'`, que é o `'0000-00-03'` desta função com outro rosto: bem
 * formado o bastante para atravessar a tela, descartado calado pelo `<input type="date">` e
 * visível só como um `422` do serviço. A faixa é a do **calendário**, e não a do mês (quem sabe
 * quantos dias aquele mês tem é `diasDoMes`, e é ela que `prazoSugerido` consulta): ano a partir
 * de 1, mês de 1 a 12 e dia de 1 a 31.
 */
export function partesDaData(partes: readonly Intl.DateTimeFormatPart[]): DataPura {
  const lidas = new Map(partes.map((parte) => [parte.type, Number(parte.value)]))
  const ano = lidas.get('year')
  const mes = lidas.get('month')
  const dia = lidas.get('day')
  if (
    ano === undefined ||
    mes === undefined ||
    dia === undefined ||
    !Number.isInteger(ano) ||
    !Number.isInteger(mes) ||
    !Number.isInteger(dia) ||
    ano < 1 ||
    mes < 1 ||
    mes > 12 ||
    dia < 1 ||
    dia > 31
  ) {
    throw new RangeError(DATA_IMPOSSIVEL)
  }
  return { ano, mes, dia }
}

/** Um número de calendário com dois dígitos — o formato que `YYYY-MM-DD` exige. */
function doisDigitos(numero: number): string {
  return String(numero).padStart(2, '0')
}

/**
 * O prazo que a sugestão preenche: hoje **em São Paulo**, mais `DIAS_DA_SUGESTAO`, como data pura
 * `YYYY-MM-DD` (4.5, FR-18, FR-22).
 *
 * **O "hoje" é o de São Paulo, e não o do navegador nem o do UTC.** Quem abre o CRM de outro fuso —
 * ou às 22h de São Paulo, que já é o dia seguinte em UTC — receberia um prazo de um dia de
 * diferença, e o prazo é o dado que decide se a Tarefa grita na tela (FR-18). Por isso as partes
 * saem de `DATA`, que é o formatador com o fuso escrito: reusá-lo mantém **um** lugar dizendo onde
 * se trabalha, em vez de um segundo `timeZone` copiado que divergiria do primeiro.
 *
 * **E a soma é feita sobre os números, sem `Date` no meio.** É a mesma defesa que
 * `dataPuraPorExtenso` faz do outro lado: `new Date('2026-09-10')` é meia-noite **UTC**, que em São
 * Paulo já é o dia 9, e qualquer volta pelo `Date` para somar três dias reabriria a porta que
 * aquela função fechou. O calendário cabe em `diasDoMes`, e a virada de mês e a de ano saem dela.
 *
 * O `while` roda uma vez com os três dias de hoje — e continua correto no dia em que
 * `DIAS_DA_SUGESTAO` crescer, que é o que um `if` não faria: ele deixaria `2026-01-31 + 40` virar
 * um mês inválido, calado.
 *
 * O instante é **parâmetro** (com o relógio como padrão) pela razão de `dataDeHojePorExtenso` no
 * shell: sem ele, a suíte confiaria no relógio de quem roda e mudaria de resultado a cada dia.
 */
export function prazoSugerido(agora: Date = new Date()): string {
  //: As três partes do dia de São Paulo, por `partesDaData` — que estoura em vez de completar com
  //: zeros o que o formatador não trouxe. O fuso é o de `DATA`, e é ele que faz "hoje" ser o dia
  //: daqui e não o do navegador.
  let { ano, mes, dia } = partesDaData(DATA.formatToParts(agora))
  dia += DIAS_DA_SUGESTAO
  while (dia > diasDoMes(ano, mes)) {
    dia -= diasDoMes(ano, mes)
    mes += 1
    if (mes > 12) {
      mes = 1
      ano += 1
    }
  }
  return `${String(ano).padStart(4, '0')}-${doisDigitos(mes)}-${doisDigitos(dia)}`
}

/* --- A tela de Tarefas e a fila do dia (4.6, FR-18, FR-20) ------------------------- */

/**
 * Os quatro filtros da tela de Tarefas, na ordem do protótipo (`:1200`) — e o recorte de cada um.
 *
 * Eles são uma **lista ordenada**, e não quatro constantes soltas, porque a tela desenha as abas
 * percorrendo-a: a ordem é a do desenho aprovado (o que dói primeiro à esquerda), e escrever as
 * quatro em quatro lugares deixaria a ordem à mercê de quem montasse o JSX.
 *
 * **`quando` é o valor do contrato, e `undefined` é a aba "Todas".** Ela não é um quarto valor do
 * enum — é a **ausência** de recorte por prazo, e é assim que ela chega ao serviço: sem o
 * parâmetro. O tipo do campo vem do contrato gerado (AD-16), então uma aba escrita com um valor
 * que o serviço não conhece não compila.
 *
 * Os rótulos são **literais do protótipo**: "Atrasadas", "Hoje", "Futuras", "Todas".
 */
export const FILTROS_DAS_TAREFAS = [
  { rotulo: 'Atrasadas', quando: 'atrasadas' },
  { rotulo: 'Hoje', quando: 'hoje' },
  { rotulo: 'Futuras', quando: 'futuras' },
  { rotulo: 'Todas', quando: undefined },
] as const satisfies readonly { rotulo: string; quando: RecorteDoPrazo | undefined }[]

/** Um dos quatro filtros da tela — o item de `FILTROS_DAS_TAREFAS`, tipado. */
export type FiltroDasTarefas = (typeof FILTROS_DAS_TAREFAS)[number]

/**
 * O texto de escopo ao lado das abas, para quem **é** da gestão — literal do protótipo (`:1201`).
 *
 * Ele é literal porque é verdade: para a gestão a policy entrega as Tarefas de todo mundo, e a
 * tela de Tarefas não filtra por Responsável nenhum (quem filtra é a fila do dia, que responde
 * outra pergunta).
 */
export const ESCOPO_DAS_TAREFAS_DA_GESTAO = 'todas as tarefas do time'

/**
 * O texto de escopo para quem **não** é da gestão — o protótipo **reescrito**, e a reescrita é a
 * decisão.
 *
 * O protótipo escreve "suas tarefas e as que você atribuiu" (`:1201`), e a segunda metade não é
 * representável: `tarefa` não tem coluna de criador, e o que a policy entrega é "as suas e as dos
 * Negócios que você enxerga". Escrever a frase do protótipo faria a tela prometer um recorte que
 * ela não aplica — e alguém procuraria aqui uma tarefa que atribuiu a um colega de outra carteira
 * e não a acharia, concluindo que o CRM perdeu a linha.
 *
 * Fica a frase que descreve o recorte **verdadeiro**. É a mesma decisão que a 4.5 tomou com a
 * promessa de relatório do modal de próximo passo: metade do protótipo é verdade, e a metade que
 * não é fica de fora até existir.
 *
 * **11.6:** a Tarefa ganhou `criador_id`, e a policy de leitura passou a entregar também as que a
 * pessoa criou — a frase acompanha o recorte novo.
 */
export const ESCOPO_DAS_TAREFAS =
  'suas tarefas, as que você criou e as dos negócios que você enxerga'

/** O vazio quando há filtro de chip ligado (11.6) — a saída é "Limpar filtros". */
export const SEM_TAREFAS_COM_FILTROS = 'Nenhuma tarefa neste recorte.'

/** O botão que desliga os seis filtros de uma vez. */
export const LIMPAR_FILTROS = 'Limpar filtros'

/** A opção fixa do Responsável e do criador: quem entrou. */
export const EU = 'Eu'

/** Os rótulos dos seis filtros da tela de Tarefas (11.6), na ordem da barra. */
export const ROTULOS_DOS_FILTROS_DAS_TAREFAS = {
  criador: 'Criador',
  responsavel: 'Responsável',
  tipo: 'Tipo',
  negocio: 'Negócio',
  empresa: 'Empresa',
  pessoa: 'Pessoa',
} as const

/** O valor "sem filtro" de cada `<select>` da barra. */
export const QUALQUER = 'Todos'

/** O nome acessível da lista da tela de Tarefas — a `<ul>` do celular e a `<table>` do computador. */
export const ROTULO_DA_TELA_DE_TAREFAS = 'Tarefas'

/**
 * Os rótulos das colunas da tela, num lugar só — no molde de `COLUNAS_DOS_ORIGINADOS`.
 *
 * Eles são **cinco**, a grade do protótipo (`:367-375`): Tipo, Empresa + título, Responsável,
 * prazo e a ação. Nenhum é de dinheiro, e a ausência é estrutural — o contrato de `GET /tarefas`
 * não tem campo de valor para perfil nenhum (FR-62).
 *
 * Eles moram aqui, e não no componente, pelo mesmo motivo de `COLUNAS_DOS_ORIGINADOS`: é **este**
 * arquivo que a varredura do FR-62 lê, e rótulo de coluna é exatamente onde uma palavra de dinheiro
 * entraria sem ninguém notar. Quem os escreve é uma árvore só — o `<thead>` do computador; o cartão
 * do celular não empilha rótulo, porque a linha dele é curta o bastante para se ler sem eles (o
 * prazo vem por extenso, e o nome sozinho já é o Responsável).
 */
export const COLUNAS_DAS_TAREFAS = {
  tipo: 'Tipo',
  titulo: 'Tarefa',
  responsavel: 'Responsável',
  vinculo: 'Negócio / empresa',
  prazo: 'Prazo',
  acao: 'Ações',
} as const

/**
 * O que a linha escreve como título quando a Tarefa **não** tem Empresa — a solta do FR-20.
 *
 * Ela é uma **frase**, e não um travessão: a Tarefa solta é um dos três vínculos que o requisito
 * concede, e um traço ali pareceria dado faltando. É o mesmo papel de `SEM_ORCAMENTO` na ficha:
 * dizer que a ausência é o estado, e não a falha.
 */
export const TAREFA_SEM_EMPRESA = 'avulsa'

/**
 * Os quatro estados vazios da tela — um por recorte, e nenhum deles é o do cartão da ficha.
 *
 * `SEM_TAREFAS` e `SEM_TAREFAS_ATRASADAS` dizem "neste negócio", e a tela não é de Negócio nenhum:
 * reusá-los aqui faria a tela de Tarefas afirmar sobre um Negócio que ela não abriu.
 *
 * **São quatro, e não um**, porque o vazio de cada aba é uma notícia diferente. "Nada atrasado" é
 * a boa notícia do dia; "nada para hoje" e "nada futuro" são constatações; e a lista **inteira**
 * vazia é o único caso em que faz sentido mandar criar — e mesmo ali a tela não tem botão de
 * criação (o protótipo também não tem), então a frase diz **onde** se cria: na ficha do Negócio.
 *
 * O tipo é `Record` sobre o rótulo de cada filtro: uma aba nova sem estado vazio não compila.
 */
export const SEM_TAREFAS_NO_RECORTE: Record<FiltroDasTarefas['rotulo'], string> = {
  Atrasadas: 'Nada atrasado. Todas as tarefas que você enxerga estão dentro do prazo.',
  Hoje: 'Nada marcado para hoje.',
  Futuras: 'Nada agendado para os próximos dias.',
  Todas:
    'Nenhuma tarefa pendente. Elas nascem na ficha de um negócio, aqui mesmo em "Nova tarefa", ' +
    'ou ao concluir uma tarefa e aceitar a sugestão do próximo passo.',
}

/* --- O prazo com hora (11.6, FR-18 emendado) ------------------------------------------- */

/**
 * O deslocamento de São Paulo. **Fixo**, e a fixação é o fato: o Brasil não tem horário de verão
 * desde 2019, e o serviço guarda o instante com o fuso que chegar. Se o país voltar a ter horário
 * de verão, é aqui — e só aqui — que o SPA muda.
 */
export const DESLOCAMENTO_DE_SAO_PAULO = '-03:00'

/** A hora que marca "o dia inteiro" — a das tarefas migradas e da sugestão (23:59:59). */
export const HORA_DO_FIM_DO_DIA = '23:59'

/**
 * O instante que o serviço recebe, a partir do que o formulário tem: a data (`2026-09-25`) e a
 * hora (`14:00`, ou vazia). **Hora vazia é o fim do dia** — a Tarefa sem hora vence às 23:59:59
 * daquele dia em São Paulo, a mesma regra das migradas.
 */
export function instanteDoPrazo(data: string, hora: string): string {
  const horaCheia = hora === '' ? `${HORA_DO_FIM_DO_DIA}:59` : `${hora}:00`
  return `${data}T${horaCheia}${DESLOCAMENTO_DE_SAO_PAULO}`
}

const PARTES_DO_INSTANTE = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
  timeZone: 'America/Sao_Paulo',
})

/** O "dia inteiro" é **exatamente** 23:59:59 — 23:59:00 é uma hora que alguém escolheu. */
const FIM_DO_DIA_COM_SEGUNDOS = `${HORA_DO_FIM_DO_DIA}:59`

/**
 * O inverso de `instanteDoPrazo`, para preencher o formulário de edição: a data e a hora **em São
 * Paulo**. Só **23:59:59** volta sem hora — é "o dia inteiro", e o campo mostra assim; 23:59:00 é
 * uma hora escolhida, e volta como `23:59`.
 */
export function partesDoPrazo(instante: string): { data: string; hora: string } {
  const quando = new Date(instante)
  if (Number.isNaN(quando.getTime())) {
    return { data: '', hora: '' }
  }
  const partes = new Map(
    PARTES_DO_INSTANTE.formatToParts(quando).map((parte) => [parte.type, parte.value]),
  )
  const data = `${partes.get('year') ?? ''}-${partes.get('month') ?? ''}-${partes.get('day') ?? ''}`
  const hora = `${partes.get('hour') ?? ''}:${partes.get('minute') ?? ''}`
  const comSegundos = `${hora}:${partes.get('second') ?? ''}`
  return { data, hora: comSegundos === FIM_DO_DIA_COM_SEGUNDOS ? '' : hora }
}

/**
 * O valor do `<input type="datetime-local">` do diálogo de Tarefa (28/09/2026), a partir do
 * instante gravado: `2026-09-25T14:00`, em São Paulo. A Tarefa **do dia inteiro** (23:59:59) volta
 * como `23:59` — o controle exige uma hora, e essa é a que o serviço já usa para "o dia inteiro".
 */
export function valorLocalDoPrazo(instante: string): string {
  const { data, hora } = partesDoPrazo(instante)
  if (data === '') {
    return ''
  }
  return `${data}T${hora === '' ? HORA_DO_FIM_DO_DIA : hora}`
}

/**
 * O inverso: o instante que o serviço recebe a partir do `datetime-local`. **`23:59` é o fim do
 * dia** (23:59:59), e não 23:59:00: é como a Tarefa sem hora chega ao campo, e reescrevê-la com
 * segundos zerados trocaria "o dia inteiro" por uma hora escolhida sem ninguém ter escolhido nada.
 * Vazio volta vazio, e é `recusaLocal` quem reclama (`FALTA_PRAZO`).
 */
export function instanteDoPrazoLocal(valor: string): string {
  const [data = '', hora = ''] = valor.split('T')
  if (data === '') {
    return ''
  }
  return instanteDoPrazo(data, hora === HORA_DO_FIM_DO_DIA ? '' : hora)
}

/** A hora com que a Tarefa nova nasce no diálogo (protótipo v2: `dia(1)`, meio-dia). */
export const HORA_PADRAO_DA_TAREFA = '12:00'

/**
 * O prazo com que o diálogo de criação nasce: **amanhã, meio-dia, em São Paulo** — o
 * `datetime-local` do protótipo já vem preenchido, e a pessoa só o troca quando o dia é outro.
 */
export function prazoPadraoDaTarefa(agora: Date = new Date()): string {
  const hoje = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(agora)
  const [ano, mes, dia] = hoje.split('-').map(Number)
  const amanha = new Date(Date.UTC(ano ?? 0, (mes ?? 1) - 1, dia ?? 1))
  amanha.setUTCDate(amanha.getUTCDate() + 1)
  return `${amanha.toISOString().slice(0, 10)}T${HORA_PADRAO_DA_TAREFA}`
}

/**
 * O prazo por extenso, para o cartão do histórico: `25/09/2026` quando é o dia inteiro e
 * `25/09/2026 às 14:00` quando há hora — sempre em São Paulo. Entrada que não é instante volta
 * pela regra da data pura (o verbatim, se nem isso for).
 */
export function prazoPorExtenso(prazoOuInstante: string): string {
  if (!prazoOuInstante.includes('T')) {
    return dataPuraPorExtenso(prazoOuInstante)
  }
  const { data, hora } = partesDoPrazo(prazoOuInstante)
  if (data === '') {
    return prazoOuInstante
  }
  const dia = dataPuraPorExtenso(data)
  return hora === '' ? dia : `${dia} às ${hora}`
}

/** O título da fila do dia, na tela Hoje — literal do protótipo (`:107`). */
export const TITULO_DA_FILA = 'A fila de hoje'

/**
 * A legenda ao lado do título da fila — literal do protótipo (`:108`).
 *
 * Ela diz as **duas** coisas que a fila promete: a ordem (o que venceu antes vem antes) e a forma
 * do prazo. Desde a 11.6 o prazo pode ter hora (`hoje, 14:00`); sem hora, vale o dia inteiro.
 */
export const LEGENDA_DA_FILA = 'vencidas primeiro · sem hora vale o dia inteiro'

/**
 * O vazio da fila do dia — e ele é a **boa notícia**, e não uma falha.
 *
 * A fila traz as pendentes com prazo até hoje. Vazia, ela quer dizer que nada venceu e nada está
 * marcado para hoje — e a frase precisa dizer isso, senão quem a lê procura o defeito. Ela é
 * distinta dos quatro vazios da tela de Tarefas pela mesma razão que aqueles são distintos entre
 * si: o recorte é outro (é a união de "atrasadas" e "hoje", e só as **suas**).
 */
export const FILA_VAZIA = 'Nada vencido e nada para hoje. A fila está limpa.'

/**
 * O que o rodapé da fila escreve quando ela foi cortada — o molde do rodapé do esfriando.
 *
 * O corte é **declarado** no serviço (`LIMITE_DA_FILA`), e a tela o diz para quem está olhando:
 * uma lista que para em seis sem uma palavra faz quem tem vinte vencidas acreditar que tem seis.
 * O número vem do **KPI**, que conta tudo — e é essa diferença que a frase existe para dizer.
 */
export function corteDaFila(mostradas: number, total: number): string {
  return `Mostrando ${NUMERO.format(mostradas)} de ${NUMERO.format(total)} tarefas até hoje.`
}

/**
 * O prazo como a linha o escreve: `hoje`, `amanhã`, `há N dias` ou `dd/MM` (protótipo `:803-812`).
 *
 * **Ela é aritmética de data pura, e `new Date(pura)` não aparece em lugar nenhum.** É a mesma
 * defesa de `dataPuraPorExtenso` e de `prazoSugerido`: `new Date('2026-09-10')` é meia-noite
 * **UTC**, que em São Paulo já é o dia 9 — e o prazo é justamente o dado que decide se a Tarefa
 * grita na tela. As duas datas viram um número de dias por uma contagem de calendário, sobre as
 * mesmas `partesDaData` e `diasDoMes` que a sugestão já usa.
 *
 * **O `hoje` é parâmetro**, e o padrão é o relógio, pela razão de `prazoSugerido`: sem ele a suíte
 * confiaria no relógio de quem roda e mudaria de resultado a cada dia. E ele é o `hoje` **de São
 * Paulo**, das partes de `DATA` — o mesmo fuso em que o serviço deriva `vencida`, para que o texto
 * e a cor da linha nunca discordem.
 *
 * **Os quatro ramos são o protótipo, e não uma escala inventada.** `há N dias` para o passado (o
 * plural sai de `dias`, de `ReguaDeDias`, para que a régua de um dia não escreva "há 1 dias"),
 * `hoje` e `amanhã` para os dois dias em que a palavra é mais legível que o número, e `dd/MM` de
 * depois de amanhã em diante — ali o número de dias já não ajuda ninguém a se localizar.
 *
 * **O passado não vira `dd/MM` nunca**, e a assimetria é do produto: uma Tarefa atrasada precisa
 * dizer **há quanto tempo**, que é a informação que faz alguém agir; uma futura precisa dizer
 * **quando**, que é a informação que faz alguém se planejar.
 *
 * O que não casa com `YYYY-MM-DD` volta **verbatim**, como em `dataPuraPorExtenso`: um texto
 * inesperado na tela é feio, e um `NaN` é pior — ele parece que a tarefa não tem prazo.
 *
 * **E a forma não basta: a faixa é a outra metade da guarda.** O `regex` aceita `2026-13-45` —
 * quatro dígitos, dois e dois —, e sem a checagem a linha escreveria "45/13" para uma data que não
 * existe, ou cairia num `diaSerial` que soma treze meses de um calendário de doze. É a mesma
 * decisão que `partesDaData` já toma do outro lado (4.5), e pelo mesmo motivo: bem formado o
 * bastante para atravessar a tela é exatamente o que ninguém vê até ser tarde. A faixa é a do
 * **calendário**, e não a do mês — quem sabe quantos dias aquele mês tem é `diasDoMes`, e um 31 de
 * fevereiro que o serviço não consegue gravar não vale uma linha de código a mais aqui.
 */
export function prazoNaLista(prazoOuInstante: string, hoje: Date = new Date()): string {
  //: 11.6: o prazo é **instante** (data e hora). O dia sai em São Paulo, e a hora só aparece
  //: quando há uma — a do fim do dia (23:59:59) é "o dia inteiro", e não se escreve.
  if (prazoOuInstante.includes('T')) {
    const { data, hora } = partesDoPrazo(prazoOuInstante)
    if (data === '') {
      return prazoOuInstante
    }
    const dia = prazoNaLista(data, hoje)
    return hora === '' ? dia : `${dia} ${hora}`
  }
  const pura = prazoOuInstante
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(pura)
  if (partes === null) {
    return pura
  }
  const prazo: DataPura = {
    ano: Number(partes[1]),
    mes: Number(partes[2]),
    dia: Number(partes[3]),
  }
  //: A faixa, e não só a forma. O verbatim é o mesmo do ramo acima: o contrato mudou debaixo da
  //: tela, e mostrar o que veio é mais honesto que inventar um dia.
  if (prazo.ano < 1 || prazo.mes < 1 || prazo.mes > 12 || prazo.dia < 1 || prazo.dia > 31) {
    return pura
  }
  //: O desenho do protótipo v2 (`prazoTexto`, 26/09/2026): "hoje" por extenso, e **dd/MM** para
  //: qualquer outro dia — passado ou futuro, sem o ano (a coluna é estreita). O atraso não se
  //: escreve em palavras ("há 4 dias"): ele é a cor vermelha da linha e a frase para o leitor de
  //: tela (`PRAZO_VENCIDO`).
  const diferenca = diasEntreDatasPuras(partesDaData(DATA.formatToParts(hoje)), prazo)
  if (diferenca === 0) {
    return 'hoje'
  }
  return `${doisDigitos(prazo.dia)}/${doisDigitos(prazo.mes)}`
}

/**
 * Quantos dias separam duas datas puras — negativo quando a segunda ficou para trás.
 *
 * **Ela conta pelo calendário, e não por milissegundos.** Converter as duas em `Date` para subtrair
 * seria reabrir a porta que `dataPuraPorExtenso` fechou, e ainda pagaria o horário de verão de
 * quebra: uma diferença em milissegundos dividida por 86.400.000 erra por um dia toda vez que um
 * dos dois lados cair numa virada de fuso.
 *
 * A conta é o **dia serial** de cada uma — quantos dias se passaram desde o começo do ano 1 —, e a
 * subtração dos dois. O dia serial usa a mesma `diasDoMes` que `prazoSugerido` consulta, e por isso
 * o 29 de fevereiro, a virada de mês e a de ano saem de um lugar só.
 *
 * Ela é exportada para que a suíte percorra as viradas sem encenar a tela quatro vezes.
 */
export function diasEntreDatasPuras(de: DataPura, ate: DataPura): number {
  return diaSerial(ate) - diaSerial(de)
}

/**
 * Quantos dias inteiros se passaram até aquela data, contando do começo do ano 1.
 *
 * O valor absoluto não significa nada — o que se usa dele é sempre a **diferença** entre dois. Ele
 * existe para que a subtração seja um número menos outro, em vez de três laços de calendário.
 *
 * A parte dos anos usa a regra completa do bissexto (o século e o quadricentenário), a mesma de
 * `ehBissexto`; a dos meses soma `diasDoMes`, que é onde fevereiro é decidido.
 */
function diaSerial({ ano, mes, dia }: DataPura): number {
  const anosCompletos = ano - 1
  //: Os bissextos até o ano anterior — a mesma regra de `ehBissexto`, contada em vez de testada.
  const bissextos =
    Math.floor(anosCompletos / 4) - Math.floor(anosCompletos / 100) + Math.floor(anosCompletos / 400)
  let total = anosCompletos * 365 + bissextos
  for (let cada = 1; cada < mes; cada += 1) {
    total += diasDoMes(ano, cada)
  }
  return total + dia
}

/* --- O vocabulário das outras telas ------------------------------------------------ */

/**
 * O rótulo de cada `status` da RAT, em português do Brasil (FR-49, FR-52).
 *
 * **Ele substitui o `SEM_RAT` que morava aqui até a 6.3**, e a troca é a história: aquela frase
 * anunciava o Épico 6 dentro de um cartão vazio, e o cartão agora lista as reclamações de verdade —
 * anunciar um épico sobre a tela que o entrega diria que ele não chegou. É a mesma troca que
 * `SEM_TAREFAS` fez na 4.2 e o cartão de Orçamento fez na 5.3.
 *
 * Ele mora aqui, e não dentro de `RatDoNegocio.tsx`, pela regra que criou este arquivo: o
 * `<select>` de status e o texto que o substitui para quem não tem a permissão escrevem os **mesmos**
 * rótulos sobre o mesmo dado, e uma segunda cópia divergiria da primeira no dia em que alguém
 * renomeasse "Em análise".
 *
 * `Record<…, string>` sobre o próprio tipo do contrato **exige os três**: um status novo no serviço
 * quebra o build aqui em vez de aparecer na tela como `em_analise`. É o mesmo molde de
 * `ROTULO_DO_STATUS`, logo abaixo.
 */
export const ROTULO_DO_STATUS_DA_RAT: Record<StatusDaRat, string> = {
  aberta: 'Aberta',
  em_analise: 'Em análise',
  resolvida: 'Resolvida',
}

/**
 * O rótulo de cada `status` do Negócio, em português do Brasil (protótipo `:249`).
 *
 * **Ele morava em `FichaDoNegocio.tsx` e subiu para cá na 3.11**, e a mudança é a mesma regra que
 * criou este arquivo: a lista de Originados escreve os **mesmos** três textos sobre o mesmo dado,
 * e uma segunda cópia divergiria da primeira no dia em que alguém renomeasse um desfecho — a badge
 * da ficha diria uma coisa e a coluna da lista, outra, com as duas suítes verdes.
 *
 * `Record<…, string>` sobre o próprio tipo do contrato **exige os três**: um status novo no serviço
 * quebra o build aqui em vez de aparecer na tela como `em_andamento`.
 */
export const ROTULO_DO_STATUS: Record<NegocioOriginado['status'], string> = {
  em_andamento: 'Em andamento',
  ganho: 'Ganho',
  perdido: 'Perdido',
}

/**
 * O estado vazio da lista de Originados (3.11) — e ele explica, em vez de só constatar.
 *
 * "Nada aqui" faria quem abre a tela pela primeira vez suspeitar de defeito. A frase diz **de onde**
 * a linha viria: o campo SDR é preenchido na criação do Negócio, e quem nunca foi escolhido nele
 * não tem o que ver. Desde a 11.5 a frase é a literal da espinha (State Patterns): a lista é
 * pós-handoff e também da gestão, e "por você" deixou de ser verdade para todos que a abrem.
 */
export const SEM_ORIGINADOS = 'Nenhum negócio originado ainda.'

/**
 * Os rótulos das colunas da lista, num lugar só.
 *
 * Eles são **seis**, e a lista é a do critério de aceite da 3.11: empresa, código, as **duas**
 * datas, desfecho e Motivo da perda. Nenhum deles é de dinheiro, e a ausência é o FR-62 escrito no
 * contrato do serviço — não há campo a esconder aqui, e por isso também não há rótulo.
 *
 * **O código é coluna própria, e não só o plano B do título.** Ele é o identificador que a operação
 * usa na boca (AD-12), e o título da linha só cai nele quando a razão social não veio: sem coluna,
 * o código sumiria justamente no caso normal, que é o de o nome ter vindo.
 *
 * **A conclusão é coluna própria, e não um sufixo do desfecho.** "Ganho" e "ganho em 10/04" são
 * respostas diferentes para quem pergunta o que aconteceu com o que originou, e o Negócio em
 * andamento escreve ali o mesmo travessão que a coluna do Motivo escreve.
 *
 * Eles moram aqui e não no componente porque as **duas** árvores da tela os escrevem: a tabela do
 * computador nos cabeçalhos, e o cartão do celular nos rótulos dos campos empilhados — que são
 * `codigo`, `inicio`, `conclusao` e `motivo`. Os outros dois viajam no **topo** do cartão, onde a
 * razão social e o chip do desfecho se nomeiam sozinhos e um rótulo em caixa-alta ao lado seria
 * ruído. Duas cópias fariam o celular chamar de "Início" o que o computador chama de "Data de
 * início" — a paridade da NFR-1 virando coincidência.
 */
export const COLUNAS_DOS_ORIGINADOS = {
  empresa: 'Empresa',
  codigo: 'Código',
  // 11.5: a SDR que originou (a visão da gestão identifica cada uma) e o Responsável **atual** —
  // "em andamento com quem". As colunas são as mesmas para todos (FR-35), e nenhuma é de dinheiro.
  sdr: 'SDR',
  responsavel: 'Responsável',
  inicio: 'Data de início',
  conclusao: 'Data de conclusão',
  desfecho: 'Desfecho',
  motivo: 'Motivo da perda',
} as const

/**
 * O que a linha escreve na coluna do Motivo quando não há Motivo — e não há na maioria delas.
 *
 * Ele é um **travessão**, e não uma frase: Negócio em andamento e Negócio ganho não têm Motivo de
 * perda por definição (o `CHECK` de coerência do desfecho o proíbe nos dois), e escrever "sem
 * motivo" sugeriria que faltou alguém preencher. É o mesmo papel que o travessão já faz na faixa
 * sem orçamento do painel.
 */
export const SEM_MOTIVO_DE_PERDA = '—'

/**
 * O que a linha escreve na coluna da conclusão quando o Negócio ainda não teve desfecho.
 *
 * Ele é **derivado** de `SEM_MOTIVO_DE_PERDA`, e não um segundo `'—'` digitado: as duas colunas
 * dizem a mesma coisa ("não há dado aqui, e não faltou alguém preencher") e uma cópia divergiria da
 * outra no primeiro ajuste — a linha mostraria travessão numa coluna e "sem data" na vizinha.
 *
 * A ausência é do `CHECK` de coerência do desfecho: `data_de_conclusao` só existe em Negócio ganho
 * ou perdido, e o em andamento **não pode** tê-la.
 */
export const SEM_DATA_DE_CONCLUSAO = SEM_MOTIVO_DE_PERDA

/**
 * O que a linha escreve quando o Motivo está gravado e o rótulo não resolveu.
 *
 * A ficha tem o texto dela (`OPCAO_FORA_DA_LISTA`), e este é o mesmo papel na lista: o último
 * degrau da escada, depois do vocabulário ativo e de `GET /opcoes-de-dominio/{id}`. Mostrar o uuid
 * cru seria a tela entregando um identificador a quem trabalha com nomes.
 */
export const MOTIVO_FORA_DA_LISTA = 'motivo não identificado'

/*
 * --- O Negócio sem Empresa nas telas (13.20b2) ---
 *
 * O Negócio que nasce da oportunidade automática (13.21a) não tem Empresa: o card, a ficha, Hoje,
 * os Originados e o diálogo de Tarefa o nomeiam pelo **contato** (o nome da Pessoa, lido em lote no
 * `cadastro` com o JWT de quem olha, AD-25) e dizem "Empresa a definir". Sem o nome visível (quem o
 * vê só pela Tarefa aberta), o título é o código do Negócio, como na degradação de sempre — nenhum
 * nome é inventado. Os textos moram aqui porque as cinco telas escrevem os mesmos.
 */

/** A linha do Negócio sem Empresa onde as telas mostrariam a Empresa (decisão do Gustavo, 26/10). */
export const EMPRESA_A_DEFINIR = 'Empresa a definir'

/** O que o leitor de tela ouve enquanto o nome do contato do Negócio sem Empresa está no ar. */
export const CONTATO_A_CAMINHO = ' (carregando o nome do contato)'

/** Por que os chevrons e o arrastar não movem o Negócio sem Empresa (a recusa `409` do serviço). */
export const DEFINA_A_EMPRESA_PARA_MOVER = 'Defina a empresa para mover o negócio.'

/** Por que "Ganho" está travado no Negócio sem Empresa (a recusa `409` do serviço). */
export const DEFINA_A_EMPRESA_PARA_GANHAR = 'Defina a empresa para marcar como ganho.'

/**
 * O aviso do "Copiar" do código do Negócio sem Empresa: o Orçamento lançado com ele no Carga
 * Máquina é recusado até a Empresa ser definida, e reprocessado sozinho na definição (13.20b1, A1).
 */
export const ORCAMENTO_SO_DEPOIS_DA_EMPRESA =
  'O orçamento deste negócio só entra depois de definir a empresa.'

/**
 * O recorte `?pessoa_id=` que falhou (code review da 13.20b2, A2): "não sei" — e não "sem Negócio".
 * A conversa e o painel não oferecem "+ Negócio" nem o vincular de antes enquanto não souberem.
 */
export const NEGOCIOS_DO_CONTATO_INDISPONIVEIS =
  'Não foi possível carregar os negócios deste contato agora.'
